/**
 * VFX Layer Engine
 * Structures visual effects into 4 cinematic layers with intensity scaling.
 * Prevents over-stacking and maintains subject clarity.
 */

import { GlobalSettings } from './engineTypes';

export type VFXIntensity = 'low' | 'medium' | 'high';

export interface VFXLayer {
  id: string;
  name: string;
  level: 1 | 2 | 3 | 4;
  effects: string[];
}

// Max active effects per layer to prevent over-stacking
const MAX_EFFECTS_PER_LAYER = 3;
const MAX_TOTAL_EFFECTS = 8;

function intensityWord(intensity: VFXIntensity): string {
  switch (intensity) {
    case 'low': return 'subtle';
    case 'medium': return 'cinematic';
    case 'high': return 'dramatic';
  }
}

function sliderActive(val: number, threshold = 20): boolean {
  return val > threshold;
}

/**
 * Build structured VFX layers from global settings.
 * Each layer is capped to prevent visual density that obscures the subject.
 */
export function buildVFXLayers(gs: GlobalSettings): VFXLayer[] {
  const layers: VFXLayer[] = [];

  // Determine overall VFX intensity from combined values
  const vfxValues = [gs.fogHaze, gs.dustParticles, gs.godRaysGlobal, gs.bloomGlow, gs.motionBlurGlobal, gs.cinematicSmoke, gs.chromaticAberration, gs.atmosphericDensity];
  const avgVFX = vfxValues.reduce((a, b) => a + b, 0) / vfxValues.length;
  const intensity: VFXIntensity = avgVFX > 50 ? 'high' : avgVFX > 20 ? 'medium' : 'low';
  const iWord = intensityWord(intensity);

  // ── Layer 1: Environmental FX ──
  const envEffects: string[] = [];
  if (sliderActive(gs.fogHaze)) envEffects.push(`${iWord} atmospheric fog and haze`);
  if (sliderActive(gs.dustParticles)) envEffects.push(`floating dust particles catching light`);
  if (gs.particles !== 'none') envEffects.push(`${gs.particles.replace(/_/g, ' ')} particles drifting in air`);
  if (sliderActive(gs.cinematicSmoke)) envEffects.push(`layered cinematic smoke wisps`);
  if (sliderActive(gs.atmosphericDensity)) envEffects.push(`dense atmospheric depth haze`);
  if (gs.fireFlame > 0) envEffects.push(`flickering fire and ember glow`);

  if (envEffects.length > 0) {
    layers.push({
      id: 'environmental',
      name: 'Environmental FX',
      level: 1,
      effects: envEffects.slice(0, MAX_EFFECTS_PER_LAYER),
    });
  }

  // ── Layer 2: Lighting FX ──
  const lightEffects: string[] = [];
  if (sliderActive(gs.godRaysGlobal)) lightEffects.push(`volumetric god rays piercing atmosphere`);
  if (sliderActive(gs.bloomGlow)) lightEffects.push(`${iWord} bloom glow on highlights`);
  if (gs.lightStreaks) lightEffects.push(`anamorphic light streaks flaring`);
  if (gs.energyAura) lightEffects.push(`ethereal energy aura emanating from subject`);
  if (gs.lightningOverlay) lightEffects.push(`dramatic lightning bolts illuminating the scene`);

  if (lightEffects.length > 0) {
    layers.push({
      id: 'lighting',
      name: 'Lighting FX',
      level: 2,
      effects: lightEffects.slice(0, MAX_EFFECTS_PER_LAYER),
    });
  }

  // ── Layer 3: Cinematic FX ──
  const cineEffects: string[] = [];
  if (sliderActive(gs.chromaticAberration)) cineEffects.push(`${iWord} chromatic aberration on edges`);
  if (gs.filmGrain) cineEffects.push(`organic analog film grain texture`);
  if (gs.glitchEffect) cineEffects.push(`digital glitch distortion artifacts`);
  if (gs.vintageMode) cineEffects.push(`vintage faded film aesthetic`);

  if (cineEffects.length > 0) {
    layers.push({
      id: 'cinematic',
      name: 'Cinematic FX',
      level: 3,
      effects: cineEffects.slice(0, MAX_EFFECTS_PER_LAYER),
    });
  }

  // ── Layer 4: Dynamic FX ──
  const dynEffects: string[] = [];
  if (sliderActive(gs.motionBlurGlobal)) dynEffects.push(`${iWord} motion blur trails`);
  if (gs.motionTrails) dynEffects.push(`kinetic motion trail streaks`);
  if (gs.explosionIntensity > 0) dynEffects.push(`pyrotechnic explosion dynamics`);

  if (dynEffects.length > 0) {
    layers.push({
      id: 'dynamic',
      name: 'Dynamic FX',
      level: 4,
      effects: dynEffects.slice(0, MAX_EFFECTS_PER_LAYER),
    });
  }

  // ── Anti-stacking: cap total effects ──
  let totalEffects = 0;
  return layers.map(layer => {
    const remaining = MAX_TOTAL_EFFECTS - totalEffects;
    if (remaining <= 0) return { ...layer, effects: [] };
    const capped = layer.effects.slice(0, remaining);
    totalEffects += capped.length;
    return { ...layer, effects: capped };
  }).filter(l => l.effects.length > 0);
}

/**
 * Flatten VFX layers into prompt-ready text segments.
 */
export function describeVFXLayers(layers: VFXLayer[]): string[] {
  if (layers.length === 0) return [];
  const parts: string[] = [];
  for (const layer of layers) {
    parts.push(layer.effects.join(', '));
  }
  return parts;
}

/**
 * Get the overall VFX intensity level from settings.
 */
export function getVFXIntensity(gs: GlobalSettings): VFXIntensity {
  const vals = [gs.fogHaze, gs.dustParticles, gs.godRaysGlobal, gs.bloomGlow, gs.motionBlurGlobal, gs.cinematicSmoke];
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  if (avg > 50) return 'high';
  if (avg > 20) return 'medium';
  return 'low';
}
